import Service from '@ember/service';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import EmberEvented from '@ember/object/evented';
import { TrackedArray } from 'tracked-built-ins';
import nowPlayingMetadata from '../utils/now-playing-metadata';
import buildFeedFunc from '../utils/build-feed';
import { next } from '@ember/runloop';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';

export default class FeedsService extends Service.extend(EmberEvented) {
  @service stereo;
  @service store;
  @service clock;
  @service router;
  // @service settings;

  @tracked feeds = new TrackedArray([]);
  @tracked currentBroadcast;
  @tracked currentPlayback;
  @tracked current;

  // Feeds built earlier in the current runloop but not yet flushed into the
  // tracked `feeds` array. Kept as a plain (non-tracked) list so buildFeed can
  // dedupe synchronously without racing the deferred push below.
  _pendingFeeds = [];

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      this.stereo.off('current-sound-changed', this.onCurrentSoundChange);
    });

    this.stereo.on('current-sound-changed', this.onCurrentSoundChange);
  }

  buildFeeds(feedRecords, options = {}) {
    this.records = feedRecords;
    return feedRecords.map(record => this.buildFeed(record, options));
  }

  findFeed(params = {}) {
    return this.feeds.find(feed => feed.matches(params));
  }

  buildFeed(feedRecord, options = {}) {
    // Dedupe against both registered feeds and any built earlier in this same
    // runloop but not yet flushed. Two synchronous buildFeed calls for one
    // record must return the same feed — a duplicate would carry its own cable
    // subscription and polling loop.
    let existingFeed =
      this.feeds.find(feed => feed.matches({feedRecord})) ||
      this._pendingFeeds.find(feed => feed.matches({feedRecord}));
    if (existingFeed) {
      return existingFeed;
    }

    let feed = buildFeedFunc(feedRecord, {...options, owner: this });
    // Register synchronously so the dedupe above can't race; defer only the
    // tracked-array push to avoid a backtracking rerender when buildFeed runs
    // during render.
    this._pendingFeeds.push(feed);
    next(() => {
      if (this.isDestroyed || this.isDestroying) return;
      if (!this.feeds.includes(feed)) {
        this.feeds.push(feed);
      }
      this._pendingFeeds = this._pendingFeeds.filter(pending => pending !== feed);
    });
    return feed;
  }

  playFeed(feedRecord, startTime) {
    this.playFeedTask.perform(feedRecord, startTime);
  }

  playFeedTask = task({restartable: true}, async (feedRecord, startTime) => {
    let feed = this.buildFeed(feedRecord);
    this.selectFeed(feed);
    await feed.playTask.perform(startTime)
  });

  // Switch playback back to the live broadcast and start it. Shared by every
  // "Go Live" control so the select-then-play sequence lives in one place. While
  // casting, broadcastFeed.playTask re-resolves the live stream onto the device.
  @action
  goLive() {
    let broadcastFeed = this.currentBroadcast;
    if (broadcastFeed) {
      this.selectFeed(broadcastFeed);
      broadcastFeed.playTask.perform();
    }
  }

  @action
  onCurrentSoundChange({sound, previousSound}) {
    // currentSound can clear to null (e.g. a sound is removed); nothing to select.
    if (!sound) {
      return;
    }
    let currentFeed = this.findFeed({url: sound.identifier});
    if (currentFeed) {
      // This allows us to toggle between playback and live feeds easily
      this.selectFeed(currentFeed);
    }
  }

  closePlayback() {
    this.stereo.stop();
    this.currentPlayback = null;
  }

  selectFeed(feed) {
    if (feed) {
      this.current = feed;
      if (feed.kind == 'broadcast') {
        this.currentBroadcast = feed;
      } else if (feed.kind == 'playback') {
        this.currentPlayback = feed;
      }
    }
  }

  updateAudioMetadata = task(async (url, recordMap) => {
    let currentMetadata = this.stereo.metadataCache.find(url);
    let {
      nowPlaying,
      airing,
      item,
      show,
      track,
      note,
      traffic,
      station,
      broadcast,
      imageSource,
      timeline,
    } = recordMap;

    item = [item, track, note, traffic]
      .filter((i) => !!i)
      .sort((a, b) => {
        return a.playedAt < b.playedAt ? 1 : -1;
      })[0];

    // select most recent item

    let objects = {
      airing,
      show: show || airing?.show,
      station: station || airing?.station,
      broadcast: broadcast || airing?.broadcast,
      item: item,
      note: undefined,
      track: undefined,
      traffic: undefined,
      itemType: item?.type,
      imageSource,
      timeline,
    };

    if (item?.type) {
      objects[item.type] = item;
    }

    let metadata = {
      ...currentMetadata,
      ...objects,
    };

    metadata = { ...metadata, ...nowPlayingMetadata(metadata) };
    // Feed layer runs on wall time (the canonical clock) — see clock.js policy.
    metadata.isLive =
      metadata?.airing?.startsAt < this.clock.date &&
      metadata?.airing?.endsAt > this.clock.date;

    this.stereo.metadataCache.store(url, metadata);
    await this.trigger('change', { url, metadata });

    return { url, metadata };
  });
}
